import { MoreIcon } from "@/components/ui/icons/MoreIcon";
import { renderTextWithMentions } from "@/utils/mentionHighlight";
import { LikeFilledIcon, LikeIcon } from "@roman/shared/components/Icons";
import {
  convertMentionsToUserIds,
  convertUserIdsToMentions,
  extractMentionsFromText,
} from "@roman/shared/features/comment/mentionParser";
import { useEditComment } from "@roman/shared/hooks/PredictionMarket/useEditComment";
import { useLikeComment } from "@roman/shared/hooks/PredictionMarket/useLikeComment";
import { useReportComment } from "@roman/shared/hooks/PredictionMarket/useReportComment";
import { useUnlikeComment } from "@roman/shared/hooks/PredictionMarket/useUnlikeComment";
import { useAuthStore } from "@roman/shared/store/auth.store";
import type {
  ChildPredictionMarketComment,
  ParentPredictionMarketComment,
  ReportReason,
} from "@roman/shared/types/Comment";
import type { PredictionMarketChoice } from "@roman/shared/types/PredictionMarket";
import { formatRelativeTimestamp } from "@roman/shared/utils";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { CommentInput } from "./CommentInput";
import { CommentMenuBottomSheet } from "./CommentMenuBottomSheet";
import { CommentReportModal } from "./CommentReportModal";
import { EntryPointsDropdown } from "./EntryPointsDropdown";
import { NewChildComment } from "./NewChildComment";

type ChildCommentProps = {
  comment: ChildPredictionMarketComment;
  isReply?: boolean;
  onShowLoginModal?: () => void;
  choices: PredictionMarketChoice[];
  refetch: () => void;
};

export const ChildComment = ({ comment, isReply = true, onShowLoginModal, choices, refetch }: ChildCommentProps) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [isReplyOpen, setIsReplyOpen] = useState(false);
  const [isEditingInline, setIsEditingInline] = useState(false);
  const [editText, setEditText] = useState(comment.text);
  const user = useAuthStore((state) => state.user);

  const userIdToNameMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const user of comment.mentionedUsers) {
      map.set(user.id, user.name);
    }
    return map;
  }, [comment.mentionedUsers]);

  const nameToUserIdMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const user of comment.mentionedUsers) {
      map.set(user.name, user.id);
    }
    return map;
  }, [comment.mentionedUsers]);

  // Convert <@userId> back to @userName for display and get mentioned names for highlighting
  const { displayText, mentionedNames } = useMemo(() => {
    const displayText = convertUserIdsToMentions(comment.text, userIdToNameMap);
    const mentionedNames = comment.mentionedUsers.map((user) => user.name);
    return { displayText, mentionedNames };
  }, [comment.text, userIdToNameMap, comment.mentionedUsers]);

  // Calculate total entry points and get choice names
  const { totalEntryPoints, entryChoices } = useMemo(() => {
    const totalPoints = comment.author.entries.reduce((total, entry) => total + Number(entry.freePoints), 0);
    const entryChoices = comment.author.entries.map((entry) => {
      const choice = choices.find((c) => c.id === entry.predictionMarketChoiceId);
      return {
        choiceName: choice?.name || "不明",
        freePoints: Number(entry.freePoints),
        choiceId: entry.predictionMarketChoiceId,
      };
    });
    return { totalEntryPoints: totalPoints, entryChoices };
  }, [comment.author.entries, choices]);

  const { isMutating: isLiking } = useLikeComment({
    onSuccess: () => {
      refetch();
    },
    onError: () => {
      toast.error("いいねに失敗しました");
    },
  });

  const { isMutating: isUnliking } = useUnlikeComment({
    onSuccess: () => {
      refetch();
    },
    onError: () => {
      toast.error("いいね取り消しに失敗しました");
    },
  });

  const { reportComment, isMutating: _isReporting } = useReportComment({
    onSuccess: () => {
      setIsReportModalOpen(false);
      toast.success("コメントを報告しました");
    },
    onError: (error) => {
      console.error("Failed to report comment:", error);
      toast.error("コメントの報告に失敗しました");
    },
  });

  const { editComment, isMutating: isEditingComment } = useEditComment({
    onSuccess: () => {
      setIsEditingInline(false);
      refetch();
      toast.success("コメントを編集しました");
    },
    onError: (error) => {
      console.error("Failed to edit comment:", error);
      toast.error("コメントの編集に失敗しました");
    },
  });

  const handleMenuClick = () => {
    setIsMenuOpen(true);
  };

  const handleReport = () => {
    setIsReportModalOpen(true);
  };

  const handleReportSubmit = (reason: ReportReason) => {
    if (!user) {
      onShowLoginModal?.();
      return;
    }
    reportComment({
      predictionMarketId: comment.predictionMarketId,
      commentId: comment.id,
      reason,
    });
  };

  const handleDelete = () => {
    refetch();
  };

  const handleReply = () => {
    if (!user) {
      onShowLoginModal?.();
      return;
    }
    setIsReplyOpen(true);
  };

  // Update editText when comment changes - use the display text for editing
  useEffect(() => {
    setEditText(displayText);
  }, [displayText]);

  const handleStartInlineEdit = () => {
    setIsEditingInline(true);
  };

  const handleSaveInlineEdit = () => {
    const trimmedText = editText.trim();
    if (trimmedText && trimmedText.length >= 1 && trimmedText.length <= 500) {
      // Convert @mentions back to <@userId> format before saving
      const textWithUserIds = convertMentionsToUserIds(trimmedText, nameToUserIdMap);
      // Extract mentioned user IDs from the text
      const mentionedUserIds = extractMentionsFromText(trimmedText, nameToUserIdMap);

      editComment({
        predictionMarketId: comment.predictionMarketId,
        commentId: comment.id,
        text: textWithUserIds,
        mentionedUserIds,
      });
    }
  };

  const handleCancelInlineEdit = () => {
    setIsEditingInline(false);
    setEditText(displayText);
  };

  return (
    <div className="">
      <div id={`comment-${comment.id}`} className={isReply ? "flex gap-[8px] mt-[12px] py-2" : "flex gap-[10px]"}>
        <img
          src={comment.author.avatar || "/assets/usericon.svg"}
          alt="avatar"
          className={
            isReply
              ? "w-[36px] h-[36px] rounded-full object-cover flex-shrink-0"
              : "w-[36px] h-[36px] rounded-full object-cover"
          }
        />
        {isReplyOpen && (
          <div
            className="border-l border-gray-100 absolute top-[44px] left-[18px] z-0"
            style={{ height: "calc(100% - 44px)" }}
          />
        )}
        <div className="flex-1">
          <div className="flex items-start justify-between">
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <span className={`font-bold ${isReply ? "text-[11px]" : "text-xs"} text-gray font-noto`}>
                  {comment.author.name}
                </span>
                {totalEntryPoints > 0 && <EntryPointsDropdown entryChoices={entryChoices} choices={choices} />}
              </div>
              <span className="text-[10px] font-medium text-light-gray font-noto">
                {formatRelativeTimestamp(new Date(comment.commentedAt).getTime())}
              </span>
            </div>
            <button
              type="button"
              onClick={handleMenuClick}
              className="p-2 md:hover:bg-light-gray/10 active:bg-light-gray/10 rounded-lg transition-colors"
            >
              <MoreIcon size={16} color="#808080" />
            </button>
          </div>
          <div className="mt-[11px]">
            {isEditingInline ? (
              <CommentInput
                setSearch={setEditText}
                onSubmit={handleSaveInlineEdit}
                onCancel={handleCancelInlineEdit}
                isSubmitting={isEditingComment}
                autoFocus={true}
                isEdit={true}
                initialValue={displayText}
                mention={mentionedNames}
              />
            ) : (
              <div className={"text-[12px] font-normal text-black"}>
                {displayText.split("\n").map((line: string, idx: number) => (
                  <span key={idx}>
                    {renderTextWithMentions(line, mentionedNames)}
                    <br />
                  </span>
                ))}
              </div>
            )}
          </div>
          <div className="flex items-center gap-[21px] mt-4">
            <div
              className={`flex items-center justify-center gap-[6px] ${
                isLiking || isUnliking ? "cursor-not-allowed opacity-50" : "cursor-pointer"
              }`}
              onClick={() => {
                window.alert("本機能はモバイルアプリでのみ利用が可能です。");
              }}
              style={{
                color: isLiking || isUnliking ? "#c0c0c0" : comment.isLikedByMe ? "#3b82f6" : "#808080",
              }}
            >
              {comment.isLikedByMe ? <LikeFilledIcon /> : <LikeIcon />}
              <div
                className="text-[10px] font-medium font-noto"
                style={{
                  color: isLiking || isUnliking ? "#c0c0c0" : comment.isLikedByMe ? "#3b82f6" : "#808080",
                }}
              >
                {comment.numberOfLikes}
              </div>
            </div>
            <div
              className="text-[10px] font-bold font-noto text-gray cursor-pointer active:bg-light-gray/10 md:hover:bg-light-gray/10 px-2 py-1 rounded"
              onClick={handleReply}
            >
              返信
            </div>
          </div>
        </div>
      </div>
      {isReplyOpen && (
        <div className="relative">
          <div className="relative animate-in fade-in-0 duration-1000">
            <NewChildComment
              comment={
                {
                  id: comment.parentId || comment.id,
                  predictionMarketId: comment.predictionMarketId,
                  authorId: comment.authorId,
                  author: comment.author,
                  text: "",
                  commentedAt: comment.commentedAt,
                  isDeleted: false,
                  numberOfLikes: comment.numberOfLikes,
                  numberOfReplies: 0,
                  isLikedByMe: comment.isLikedByMe,
                  mentionedUserIds: [],
                  mentionedUsers: [],
                } as ParentPredictionMarketComment
              }
              onReplySuccess={() => {
                refetch();
              }}
              onCancel={() => {
                setIsReplyOpen(false);
              }}
            />
          </div>
          <div
            className="w-[3px] absolute top-[36px] left-[18px] z-2 bg-white"
            style={{
              height: "calc(100% - 36px)",
            }}
          />
        </div>
      )}
      <CommentMenuBottomSheet
        open={isMenuOpen}
        setOpen={setIsMenuOpen}
        comment={comment}
        onReport={handleReport}
        onDeleteSuccess={handleDelete}
        onStartInlineEdit={handleStartInlineEdit}
      />

      <CommentReportModal
        open={isReportModalOpen}
        setOpen={setIsReportModalOpen}
        commentId={comment.id}
        onReportSubmit={handleReportSubmit}
      />
    </div>
  );
};
